import './admin-avg-video.css';
import { state } from '../shared/state';
import { $, showToast, renderMarkdownHtml, bindAutoGrowTextareas, fitAutoGrowTextareas } from '../shared/index';
import {
  AVG_VIDEO_STATUS_LABELS,
  escapeHtml,
  formatDuration,
  formatVideoDate,
  formatViewCount,
  parseItems,
  posterSrc,
  tagsLine,
  type AvgVideoSeason,
  type AvgVideoStatus,
  type AvgVideoVideo,
} from '../avg-video/avg-video-shared';

let adminVideosCache: AvgVideoVideo[] = [];
let adminStatsCache: Record<AvgVideoStatus, number> | null = null;
let editingVideoId: string | null = null;
let adminFiltersBound = false;
let selectedIds = new Set<string>();

interface Rubric {
  id: string;
  parent_id: string | null;
  title: string;
  sort_order: number;
  video_count: number;
  published_count: number;
}

/** Подборка/сериал в объёме, нужном карточке видео (полный тип — в редакторе подборок). */
interface CollectionBrief {
  id: string;
  kind: 'series' | 'thematic';
  title: string;
  status: 'draft' | 'published';
  sort_order: number;
}

let adminRubricsCache: Rubric[] = [];
let adminCollectionsCache: CollectionBrief[] = [];

/* Состояние формы размещения: чекбоксы перерисовываются (смена направления,
   фильтр поиска), поэтому выбор держится в наборах, а не в самом DOM. */
let editorSubrubricIds = new Set<string>();
let editorCollectionIds = new Set<string>();
let editorRelatedIds = new Set<string>();
/** Сезоны выбранного в карточке сериала; перезагружаются при смене сериала. */
let editorSeasons: AvgVideoSeason[] = [];

/**
 * Реквизиты внешнего хранилища (раздел «Видео» → «Хранилище»). Токен целиком
 * с сервера не приходит — только has_token/token_masked (см.
 * avg-video-storage.controller.ts toPublicSettings). В поле ввода токен
 * никогда не подставляется и никуда не логируется.
 */
type StorageCredentialSource = 'db' | 'env' | 'default' | null;

/**
 * Остаток места на самом Яндекс.Диске (AV-ADR-1, AV-HC-6). До этого этапа
 * остаток не проверял никто: о том, что хранилище кончилось, узнавали по
 * отказу выгрузки.
 */
interface AvgVideoDiskSpace {
  total_bytes: number;
  used_bytes: number;
  free_bytes: number;
  free_percent: number;
  low: boolean;
  low_threshold_percent: number;
}

interface AvgVideoStorageSettings {
  root_path: string | null;
  /** Действующий корневой каталог: сохранённый, иначе из env, иначе умолчание. */
  root_path_effective?: string;
  root_source?: StorageCredentialSource;
  /** Остаток места на самом Диске (AV-HC-6); null — Диск не ответил. */
  disk_space?: AvgVideoDiskSpace | null;
  has_token: boolean;
  /** Откуда взят действующий токен: сохранённый в админке или из env сервера. */
  token_source?: StorageCredentialSource;
  token_masked: string | null;
  updated_at: number;
}

/* Аннотация пишется в markdown — куратор видит её так же, как пользователь. */
function renderAnnotationCell(video: AvgVideoVideo): string {
  if (!video.annotation?.trim()) return '';
  return `
    <details class="atv-admin-anno">
      <summary>Аннотация</summary>
      <div class="atv-md">${renderMarkdownHtml(video.annotation)}</div>
    </details>
  `;
}

/* Теги показываются как есть: деления на «теги-категории» и прочие больше нет,
   направление задаёт рубрика. Запасной пилюли с категорией тоже нет — у видео
   этого поля не существует, а пустой столбец честнее выдуманного значения. */
function renderTagPills(video: AvgVideoVideo): string {
  const pills = (video.tags || [])
    .map((t) => `<span class="atv-tag-pill atv-tag-pill--muted">${escapeHtml(t)}</span>`);
  return pills.join(' ') || '<span class="atv-admin-sub">—</span>';
}

/* Теги — одно свободное поле (AV-ADR-2, решение 4). Чекбоксов «теги-категории»
   больше нет: направление задаётся рубрикой, а не тегом. Существующие теги не
   мигрируются и правятся здесь как есть. */
function setTagsField(tags: string[]): void {
  const el = $<HTMLInputElement>('admin-atv-tags');
  if (el) el.value = tags.join(', ');
}

function readTagsField(): string[] {
  const raw = $<HTMLInputElement>('admin-atv-tags')?.value || '';
  return raw.split(/[,#]+/).map((t) => t.trim()).filter(Boolean);
}

function statusClass(status: AvgVideoStatus): string {
  return `atv-status-badge atv-status-${status}`;
}

async function loadRubrics(): Promise<void> {
  try {
    const r = await fetch('/api/admin/avg-video/rubrics', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось загрузить рубрики');
    // Контракт рубрик: `{ items, total }` — общая форма для публичного и
    // админского путей.
    const data = await r.json() as { items?: Rubric[]; total?: number };
    adminRubricsCache = data.items || [];
    renderRubricsTree();
    updateRubricsFilter();
    renderDirectionSelect();
    renderSubrubrics();
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

/**
 * Что сейчас правится в дереве рубрик. Раньше это были `prompt()`/`confirm()`:
 * ввод названия модальным окном браузера выглядел в админке чужеродно и не
 * показывал, к какой строке относится вопрос.
 */
type RubricInline =
  | { mode: 'idle' }
  | { mode: 'add'; parentId: string | null }
  | { mode: 'rename'; id: string }
  | { mode: 'delete'; id: string };

let rubricInline: RubricInline = { mode: 'idle' };

function rubricFormHtml(action: string, value: string, placeholder: string): string {
  return `
    <div class="atv-inline-form">
      <input type="text" id="admin-atv-rubric-input" class="setup-input" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}">
      <button type="button" class="atv-mini-btn atv-mini-btn--primary" data-atv-rubric-apply="${escapeHtml(action)}">Сохранить</button>
      <button type="button" class="atv-mini-btn" data-atv-rubric-cancel="1">Отмена</button>
    </div>
  `;
}

function rubricDeleteConfirmHtml(rubric: Rubric): string {
  const childCount = adminRubricsCache.filter((c) => c.parent_id === rubric.id).length;
  const note = childCount > 0 ? ` вместе с подрубриками (${childCount})` : '';
  return `
    <div class="atv-inline-confirm">
      <span>Удалить «${escapeHtml(rubric.title)}»${note}?</span>
      <button type="button" class="atv-mini-btn atv-mini-btn--danger" data-atv-rubric-apply="delete">Удалить</button>
      <button type="button" class="atv-mini-btn" data-atv-rubric-cancel="1">Отмена</button>
    </div>
  `;
}

/** Рубрики одного уровня в том же порядке, в каком их рисует дерево. */
function rubricSiblings(parentId: string | null): Rubric[] {
  return adminRubricsCache
    .filter((r) => (r.parent_id || null) === (parentId || null))
    .sort((a, b) => a.sort_order - b.sort_order);
}

function rubricRowHtml(rubric: Rubric, isChild: boolean): string {
  if (rubricInline.mode === 'rename' && rubricInline.id === rubric.id) {
    return rubricFormHtml('rename', rubric.title, 'Название рубрики');
  }
  if (rubricInline.mode === 'delete' && rubricInline.id === rubric.id) {
    return rubricDeleteConfirmHtml(rubric);
  }

  const addChild = isChild
    ? ''
    : `<button type="button" class="atv-icon-btn" data-atv-rubric-add-child="${escapeHtml(rubric.id)}" title="Добавить подрубрику">＋</button>`;

  // Порядок правится только внутри своего уровня: направление не может уехать
  // в чужой список подрубрик, поэтому у крайних строк кнопка выключена.
  const siblings = rubricSiblings(rubric.parent_id);
  const index = siblings.findIndex((r) => r.id === rubric.id);
  const move = `
    <button type="button" class="atv-icon-btn" data-atv-rubric-up="${escapeHtml(rubric.id)}" title="Выше"${index <= 0 ? ' disabled' : ''}>↑</button>
    <button type="button" class="atv-icon-btn" data-atv-rubric-down="${escapeHtml(rubric.id)}" title="Ниже"${index >= siblings.length - 1 ? ' disabled' : ''}>↓</button>
  `;

  return `
    <div class="atv-rubric-row${isChild ? ' atv-rubric-row--child' : ''}">
      <div class="atv-rubric-info">
        <span class="atv-rubric-title">${isChild ? '— ' : ''}${escapeHtml(rubric.title)}</span>
        <span class="atv-rubric-count">${rubric.video_count}</span>
      </div>
      <div class="atv-rubric-actions">
        ${move}
        ${addChild}
        <button type="button" class="atv-icon-btn" data-atv-rubric-edit="${escapeHtml(rubric.id)}" title="Переименовать">✏️</button>
        <button type="button" class="atv-icon-btn" data-atv-rubric-delete="${escapeHtml(rubric.id)}" title="Удалить">🗑️</button>
      </div>
    </div>
  `;
}

function renderRubricsTree(): void {
  const container = $('admin-atv-rubrics-tree');
  if (!container) return;

  const rootRubrics = adminRubricsCache.filter((r) => !r.parent_id).sort((a, b) => a.sort_order - b.sort_order);

  const rows = rootRubrics.map((r) => {
    const children = adminRubricsCache.filter((c) => c.parent_id === r.id).sort((a, b) => a.sort_order - b.sort_order);
    const childForm = rubricInline.mode === 'add' && rubricInline.parentId === r.id
      ? rubricFormHtml('create', '', 'Название подрубрики')
      : '';
    return rubricRowHtml(r, false) + children.map((c) => rubricRowHtml(c, true)).join('') + childForm;
  }).join('');

  const rootForm = rubricInline.mode === 'add' && rubricInline.parentId === null
    ? rubricFormHtml('create', '', 'Название рубрики')
    : '';

  container.innerHTML = rows + rootForm
    || '<div class="audit-log-empty">Рубрик пока нет</div>';

  $<HTMLInputElement>('admin-atv-rubric-input')?.focus();
}

function updateRubricsFilter(): void {
  const select = $<HTMLSelectElement>('admin-atv-filter-rubric');
  if (!select) return;

  const currentValue = select.value;
  const rootRubrics = adminRubricsCache.filter((r) => !r.parent_id).sort((a, b) => a.sort_order - b.sort_order);

  const childOptions = rootRubrics.flatMap((r) => {
    const children = adminRubricsCache.filter((c) => c.parent_id === r.id).sort((a, b) => a.sort_order - b.sort_order);
    const opts: string[] = [`<option value="${escapeHtml(r.id)}">${escapeHtml(r.title)}</option>`];
    opts.push(...children.map((c) => `<option value="${escapeHtml(c.id)}">— ${escapeHtml(c.title)}</option>`));
    return opts;
  });

  select.innerHTML = '<option value="">Все рубрики</option>' + childOptions.join('');
  select.value = currentValue;
}

function rootRubrics(): Rubric[] {
  return adminRubricsCache.filter((r) => !r.parent_id).sort((a, b) => a.sort_order - b.sort_order);
}

/** Направление карточки = корневая рубрика. Второго списка направлений нет. */
function renderDirectionSelect(): void {
  const select = $<HTMLSelectElement>('admin-atv-direction');
  if (!select) return;
  const current = select.value;
  select.innerHTML = '<option value="">— не выбрано —</option>'
    + rootRubrics().map((r) => `<option value="${escapeHtml(r.id)}">${escapeHtml(r.title)}</option>`).join('');
  select.value = current;
}

function renderSubrubrics(): void {
  const container = $('admin-atv-subrubrics');
  if (!container) return;

  const directionId = $<HTMLSelectElement>('admin-atv-direction')?.value || '';
  if (!directionId) {
    container.innerHTML = '<span class="atv-check-empty">Сначала выберите направление</span>';
    return;
  }

  const children = adminRubricsCache
    .filter((c) => c.parent_id === directionId)
    .sort((a, b) => a.sort_order - b.sort_order);

  if (children.length === 0) {
    container.innerHTML = '<span class="atv-check-empty">У этого направления нет подрубрик</span>';
    return;
  }

  container.innerHTML = children.map((c) => `
    <label class="atv-check-option">
      <input type="checkbox" class="admin-atv-subrubric-check" value="${escapeHtml(c.id)}"${editorSubrubricIds.has(c.id) ? ' checked' : ''}>
      <span>${escapeHtml(c.title)}</span>
    </label>
  `).join('');
}

/* ── Размещение: подборки, сериал, сезоны, связанные видео ───────────── */

async function loadCollectionsForEditor(): Promise<void> {
  try {
    const r = await fetch('/api/admin/avg-video/collections', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось загрузить подборки');
    adminCollectionsCache = parseItems<CollectionBrief>(await r.json());
  } catch {
    adminCollectionsCache = [];
  }
  renderSeriesSelect();
  renderThematicChecks();
}

function renderSeriesSelect(): void {
  const select = $<HTMLSelectElement>('admin-atv-series');
  if (!select) return;
  const current = select.value;
  const series = adminCollectionsCache.filter((c) => c.kind === 'series')
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'ru'));
  select.innerHTML = '<option value="">— вне сериала —</option>'
    + series.map((c) => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.title)}</option>`).join('');
  select.value = current;
}

function renderThematicChecks(): void {
  const container = $('admin-atv-collections');
  if (!container) return;

  const thematic = adminCollectionsCache.filter((c) => c.kind === 'thematic')
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'ru'));

  if (thematic.length === 0) {
    container.innerHTML = '<span class="atv-check-empty">Тематических подборок пока нет</span>';
    return;
  }

  container.innerHTML = thematic.map((c) => `
    <label class="atv-check-option">
      <input type="checkbox" class="admin-atv-collection-check" value="${escapeHtml(c.id)}"${editorCollectionIds.has(c.id) ? ' checked' : ''}>
      <span>${escapeHtml(c.title)}${c.status === 'draft' ? ' <span class="atv-check-empty">(черновик)</span>' : ''}</span>
    </label>
  `).join('');
}

/** Сезоны нужного сериала; список перезагружается при каждой смене сериала. */
async function loadEditorSeasons(seriesId: string, keepSeasonId: string | null): Promise<void> {
  editorSeasons = [];
  if (seriesId) {
    try {
      const r = await fetch(`/api/admin/avg-video/collections/${encodeURIComponent(seriesId)}/seasons`, {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (r.ok) {
        const data = await r.json() as { items?: AvgVideoSeason[] };
        editorSeasons = data.items || [];
      }
    } catch {
      editorSeasons = [];
    }
  }
  renderSeasonSelect(keepSeasonId);
}

function seasonOptionLabel(season: AvgVideoSeason): string {
  return season.title ? `Сезон ${season.number} · ${season.title}` : `Сезон ${season.number}`;
}

function renderSeasonSelect(selectedId: string | null): void {
  const select = $<HTMLSelectElement>('admin-atv-season');
  if (!select) return;
  select.innerHTML = '<option value="">— без сезона —</option>'
    + editorSeasons
      .slice()
      .sort((a, b) => a.number - b.number)
      .map((s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(seasonOptionLabel(s))}</option>`)
      .join('');
  select.value = selectedId && editorSeasons.some((s) => s.id === selectedId) ? selectedId : '';
  select.disabled = !$<HTMLSelectElement>('admin-atv-series')?.value;
}

function renderRelatedList(): void {
  const container = $('admin-atv-related');
  if (!container) return;

  const query = ($<HTMLInputElement>('admin-atv-related-search')?.value || '').trim().toLowerCase();
  // Выбранные показываем всегда, даже если не подходят под фильтр: иначе снять
  // связь после поиска стало бы невозможно.
  const list = adminVideosCache
    .filter((v) => v.id !== editingVideoId)
    .filter((v) => !query || v.title.toLowerCase().includes(query) || editorRelatedIds.has(v.id))
    .sort((a, b) => a.title.localeCompare(b.title, 'ru'));

  if (list.length === 0) {
    container.innerHTML = '<span class="atv-check-empty">Ничего не найдено</span>';
    return;
  }

  container.innerHTML = list.map((v) => `
    <label class="atv-check-option">
      <input type="checkbox" class="admin-atv-related-check" value="${escapeHtml(v.id)}"${editorRelatedIds.has(v.id) ? ' checked' : ''}>
      <span>${escapeHtml(v.title)}</span>
    </label>
  `).join('');
}

function filteredVideos(): AvgVideoVideo[] {
  const search = ($<HTMLInputElement>('admin-atv-search')?.value || '').trim().toLowerCase();
  const status = $<HTMLSelectElement>('admin-atv-filter-status')?.value || '';
  const rubric = $<HTMLSelectElement>('admin-atv-filter-rubric')?.value || '';
  const sort = $<HTMLSelectElement>('admin-atv-filter-sort')?.value || 'newest';

  let list = [...adminVideosCache];
  if (status) list = list.filter((v) => v.status === status);
  if (rubric) list = list.filter((v) => (v.rubric_ids || []).includes(rubric));
  if (search) {
    list = list.filter((v) =>
      v.title.toLowerCase().includes(search)
      || (v.description || '').toLowerCase().includes(search)
      || (v.annotation || '').toLowerCase().includes(search)
      || tagsLine(v.tags).toLowerCase().includes(search)
    );
  }

  switch (sort) {
    case 'oldest':
      list.sort((a, b) => a.created_at - b.created_at);
      break;
    case 'views':
      list.sort((a, b) => b.view_count - a.view_count);
      break;
    case 'order':
      list.sort((a, b) => a.sort_order - b.sort_order);
      break;
    default:
      list.sort((a, b) => b.updated_at - a.updated_at);
  }
  return list;
}

function updateStats(): void {
  const stats = adminStatsCache;
  if (!stats) return;
  const set = (id: string, val: number) => {
    const el = $(id);
    if (el) el.textContent = String(val);
  };
  set('admin-atv-stat-published', stats.published);
  set('admin-atv-stat-review', stats.review);
  set('admin-atv-stat-scheduled', stats.scheduled);
  set('admin-atv-stat-draft', stats.draft);

  const summary = $('admin-atv-summary');
  if (summary) {
    const total = Object.values(stats).reduce((a, b) => a + b, 0);
    const pending = stats.review + stats.scheduled + stats.draft;
    summary.textContent = `Управление публикацией · ${total} видео · ${pending} ожидают`;
  }
}

function renderTable(): void {
  const tbody = $('admin-atv-table-body');
  if (!tbody) return;

  const list = filteredVideos();
  if (list.length === 0) {
    tbody.innerHTML = '<tr><td colspan="7" class="audit-log-empty">Видео не найдены</td></tr>';
    return;
  }

  tbody.innerHTML = list.map((v) => {
    const meta = [
      formatDuration(v.duration_seconds),
      v.example_label ? `Маршрут ${v.example_label}` : null,
      v.tags?.length ? tagsLine(v.tags) : null,
    ].filter(Boolean).join(' · ');

    let actions = `
      <button type="button" class="atv-icon-btn" data-atv-edit="${escapeHtml(v.id)}" title="Редактировать">✏️</button>
    `;

    if (v.status === 'scheduled') {
      actions = `
        <button type="button" class="atv-mini-btn atv-mini-btn--primary" data-atv-status="${escapeHtml(v.id)}" data-status="published">Опубликовать сейчас</button>
        ${actions}
      `;
    } else if (v.status === 'review') {
      actions = `
        <button type="button" class="atv-mini-btn atv-mini-btn--success" data-atv-status="${escapeHtml(v.id)}" data-status="published">Одобрить</button>
        <button type="button" class="atv-mini-btn atv-mini-btn--muted" data-atv-status="${escapeHtml(v.id)}" data-status="draft">Отклонить</button>
      `;
    } else if (v.status === 'draft') {
      actions = `
        <button type="button" class="atv-mini-btn atv-mini-btn--primary" data-atv-edit="${escapeHtml(v.id)}">Продолжить</button>
        <button type="button" class="atv-icon-btn" data-atv-delete="${escapeHtml(v.id)}" title="Удалить">🗑️</button>
      `;
    } else if (v.status === 'published') {
      actions += `
        <button type="button" class="atv-icon-btn" data-atv-status="${escapeHtml(v.id)}" data-status="archived" title="Снять с публикации">⊘</button>
      `;
    } else {
      actions += `<button type="button" class="atv-icon-btn" data-atv-delete="${escapeHtml(v.id)}" title="Удалить">🗑️</button>`;
    }

    const dateCell = v.status === 'scheduled' && v.scheduled_at
      ? `<div>${escapeHtml(formatVideoDate(v.scheduled_at))}</div><div class="atv-admin-sub">${new Date(v.scheduled_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} МСК</div>`
      : escapeHtml(formatVideoDate(v.published_at || v.updated_at));

    return `
      <tr>
        <td><input type="checkbox" class="admin-atv-row-check" data-id="${escapeHtml(v.id)}"${selectedIds.has(v.id) ? ' checked' : ''}></td>
        <td>
          <div class="atv-admin-video-cell">
            <div class="atv-admin-thumb">
              ${posterSrc(v) ? `<img src="${escapeHtml(posterSrc(v)!)}" alt="">` : playSvg()}
            </div>
            <div>
              <div class="font-medium">${v.is_featured ? '<span class="atv-featured-star" title="Рекомендованное видео каталога">★</span> ' : ''}${escapeHtml(v.title)}</div>
              <div class="atv-admin-sub">${escapeHtml(meta)}</div>
              ${renderAnnotationCell(v)}
            </div>
          </div>
        </td>
        <td><div class="atv-admin-tag-cell">${renderTagPills(v)}</div></td>
        <td><span class="${statusClass(v.status)}">${escapeHtml(AVG_VIDEO_STATUS_LABELS[v.status])}</span></td>
        <td>${dateCell}</td>
        <td style="text-align:right">${v.status === 'published' ? formatViewCount(v.view_count) : '—'}</td>
        <td><div class="atv-admin-actions">${actions}</div></td>
      </tr>
    `;
  }).join('');

  const countEl = $('admin-atv-shown-count');
  if (countEl) countEl.textContent = `Показано ${list.length} из ${adminVideosCache.length}`;
  updateBulkBar();
}

function playSvg(): string {
  return '<svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
}

function updateBulkBar(): void {
  const bar = $('admin-atv-bulk');
  const countEl = $('admin-atv-selected-count');
  if (countEl) countEl.textContent = String(selectedIds.size);
  if (bar) bar.classList.toggle('hidden', selectedIds.size === 0);
}

function showEditor(show: boolean): void {
  $('admin-atv-editor')?.classList.toggle('hidden', !show);
  $('admin-atv-list-panel')?.classList.toggle('hidden', show);
}

function resetEditor(): void {
  editingVideoId = null;
  $<HTMLInputElement>('admin-atv-title')!.value = '';
  $<HTMLTextAreaElement>('admin-atv-description')!.value = '';
  $<HTMLTextAreaElement>('admin-atv-annotation')!.value = '';
  $<HTMLSelectElement>('admin-atv-status')!.value = 'draft';
  $<HTMLInputElement>('admin-atv-video-url')!.value = '';
  $<HTMLInputElement>('admin-atv-thumbnail-url')!.value = '';
  $<HTMLInputElement>('admin-atv-preview-url')!.value = '';
  $<HTMLInputElement>('admin-atv-duration-seconds')!.value = '';
  setTagsField([]);
  $<HTMLInputElement>('admin-atv-route-label')!.value = '';
  $<HTMLInputElement>('admin-atv-sort')!.value = '0';
  $<HTMLInputElement>('admin-atv-featured')!.checked = false;
  $<HTMLInputElement>('admin-atv-free')!.checked = false;
  $<HTMLInputElement>('admin-atv-scheduled-at')!.value = '';
  $<HTMLSelectElement>('admin-atv-direction')!.value = '';
  $<HTMLSelectElement>('admin-atv-series')!.value = '';
  $<HTMLInputElement>('admin-atv-episode')!.value = '';
  $<HTMLInputElement>('admin-atv-related-search')!.value = '';
  editorSubrubricIds = new Set();
  editorCollectionIds = new Set();
  editorRelatedIds = new Set();
  editorSeasons = [];
  renderSubrubrics();
  renderThematicChecks();
  renderSeasonSelect(null);
  renderRelatedList();
}

async function openEditor(id: string | null): Promise<void> {
  resetEditor();
  editingVideoId = id;

  if (id) {
    let video = adminVideosCache.find((v) => v.id === id);
    if (!video) {
      const r = await fetch(`/api/admin/avg-video/videos/${encodeURIComponent(id)}`, {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (!r.ok) {
        showToast('❌ Не удалось загрузить видео');
        return;
      }
      video = await r.json() as AvgVideoVideo;
    }
    $<HTMLInputElement>('admin-atv-title')!.value = video.title;
    $<HTMLTextAreaElement>('admin-atv-description')!.value = video.description || '';
    $<HTMLTextAreaElement>('admin-atv-annotation')!.value = video.annotation || '';
    $<HTMLSelectElement>('admin-atv-status')!.value = video.status;
    $<HTMLInputElement>('admin-atv-video-url')!.value = video.video_url || '';
    $<HTMLInputElement>('admin-atv-thumbnail-url')!.value = video.thumbnail_url || '';
    $<HTMLInputElement>('admin-atv-preview-url')!.value = video.preview_url || '';
    $<HTMLInputElement>('admin-atv-duration-seconds')!.value = video.duration_seconds != null ? String(video.duration_seconds) : '';
    setTagsField(video.tags || []);
    $<HTMLInputElement>('admin-atv-route-label')!.value = video.example_label || '';
    $<HTMLInputElement>('admin-atv-sort')!.value = String(video.sort_order ?? 0);
    $<HTMLInputElement>('admin-atv-featured')!.checked = video.is_featured;
    $<HTMLInputElement>('admin-atv-free')!.checked = video.is_free_access || false;
    if (video.scheduled_at) {
      const d = new Date(video.scheduled_at);
      $<HTMLInputElement>('admin-atv-scheduled-at')!.value = d.toISOString().slice(0, 16);
    }

    // Направление — первая корневая рубрика видео; остальные привязки этого
    // направления становятся подрубриками.
    const linked = new Set(video.rubric_ids || []);
    const direction = rootRubrics().find((r) => linked.has(r.id));
    $<HTMLSelectElement>('admin-atv-direction')!.value = direction?.id || '';
    editorSubrubricIds = new Set(
      adminRubricsCache.filter((r) => r.parent_id && linked.has(r.id)).map((r) => r.id)
    );
    renderSubrubrics();

    editorCollectionIds = new Set(video.collection_ids || []);
    renderThematicChecks();
    editorRelatedIds = new Set(video.related_video_ids || []);
    renderRelatedList();

    $<HTMLSelectElement>('admin-atv-series')!.value = video.series_id || '';
    // Поле подписано «Номер серии»: наружу номер идёт с единицы, в базе позиция с нуля.
    $<HTMLInputElement>('admin-atv-episode')!.value = video.episode_position != null ? String(video.episode_position + 1) : '';
    await loadEditorSeasons(video.series_id || '', video.season_id || null);

    const title = $('admin-atv-editor-title');
    if (title) title.textContent = 'Редактировать видео';
  } else {
    const title = $('admin-atv-editor-title');
    if (title) title.textContent = 'Новое видео';
  }

  showEditor(true);
  // Описание и аннотация пишутся в markdown — поля подгоняются под текст.
  fitAutoGrowTextareas($('admin-atv-editor') || document);
}

/** Тело upsert карточки: имена полей — по контракту каталога видео (v1). */
interface VideoUpsertPayload {
  title: string;
  description: string | null;
  annotation: string | null;
  status: AvgVideoStatus;
  video_url: string | null;
  thumbnail_url: string | null;
  preview_url: string | null;
  duration_seconds: number | null;
  tags: string[];
  /** Подпись связанного маршрута; открыть конкретный маршрут по id экран
   *  «Примеры» не умеет, поэтому идентификатора у связи больше нет. */
  example_label: string | null;
  is_featured: boolean;
  is_free_access: boolean;
  sort_order: number;
  scheduled_at: number | null;
  rubric_ids: string[];
  series_id: string | null;
  season_id: string | null;
  episode_position: number | null;
  collection_ids: string[];
  related_video_ids: string[];
}

function readEditorPayload(): VideoUpsertPayload {
  const scheduledRaw = $<HTMLInputElement>('admin-atv-scheduled-at')?.value;
  const durationSecRaw = $<HTMLInputElement>('admin-atv-duration-seconds')?.value;
  const episodeRaw = $<HTMLInputElement>('admin-atv-episode')?.value;

  const directionId = $<HTMLSelectElement>('admin-atv-direction')?.value || '';
  const subrubricIds = adminRubricsCache
    .filter((r) => r.parent_id === directionId && editorSubrubricIds.has(r.id))
    .map((r) => r.id);

  const seriesId = $<HTMLSelectElement>('admin-atv-series')?.value || '';
  const seasonId = $<HTMLSelectElement>('admin-atv-season')?.value || '';

  const payload: VideoUpsertPayload = {
    title: $<HTMLInputElement>('admin-atv-title')?.value.trim() || '',
    description: $<HTMLTextAreaElement>('admin-atv-description')?.value.trim() || null,
    annotation: $<HTMLTextAreaElement>('admin-atv-annotation')?.value.trim() || null,
    status: ($<HTMLSelectElement>('admin-atv-status')?.value || 'draft') as AvgVideoStatus,
    video_url: $<HTMLInputElement>('admin-atv-video-url')?.value.trim() || null,
    thumbnail_url: $<HTMLInputElement>('admin-atv-thumbnail-url')?.value.trim() || null,
    preview_url: $<HTMLInputElement>('admin-atv-preview-url')?.value.trim() || null,
    duration_seconds: durationSecRaw ? parseInt(durationSecRaw, 10) : null,
    tags: readTagsField(),
    example_label: $<HTMLInputElement>('admin-atv-route-label')?.value.trim() || null,
    is_featured: $<HTMLInputElement>('admin-atv-featured')?.checked === true,
    is_free_access: $<HTMLInputElement>('admin-atv-free')?.checked === true,
    sort_order: parseInt($<HTMLInputElement>('admin-atv-sort')?.value || '0', 10) || 0,
    scheduled_at: scheduledRaw ? new Date(scheduledRaw).getTime() : null,
    rubric_ids: directionId ? [directionId, ...subrubricIds] : subrubricIds,
    series_id: seriesId || null,
    // season_id без series_id сервер отвергает (400) — не отправляем осиротевший сезон.
    season_id: seriesId ? (seasonId || null) : null,
    episode_position: episodeRaw ? Math.max(0, parseInt(episodeRaw, 10) - 1) : null,
    collection_ids: [...editorCollectionIds],
    related_video_ids: [...editorRelatedIds],
  };

  return payload;
}

async function saveEditor(): Promise<void> {
  const payload = readEditorPayload();
  if (!payload.title) {
    showToast('❌ Укажите название видео');
    return;
  }  if (payload.status === 'scheduled') {
    if (!payload.scheduled_at) {
      showToast('❌ Укажите дату и время публикации');
      return;
    }
    if (payload.scheduled_at < Date.now()) {
      showToast('❌ Дата публикации должна быть в будущем');
      return;
    }
  }

  if ((payload.status === 'published' || payload.status === 'scheduled') && !payload.video_url) {
    showToast('❌ Укажите ссылку на видео');
    return;
  }

  const url = editingVideoId
    ? `/api/admin/avg-video/videos/${encodeURIComponent(editingVideoId)}`
    : '/api/admin/avg-video/videos';
  const method = editingVideoId ? 'PUT' : 'POST';

  const r = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { detail?: string; message?: string };
    showToast('❌ ' + (err.detail || err.message || 'Ошибка сохранения'));
    return;
  }

  showToast('✅ Видео сохранено');
  showEditor(false);
  await loadAdminAvgVideo();
}

async function patchStatusRequest(id: string, status: AvgVideoStatus): Promise<{ ok: boolean; message?: string }> {
  const r = await fetch(`/api/admin/avg-video/videos/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ status }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({})) as { detail?: string; message?: string };
    return { ok: false, message: err.detail || err.message || 'Не удалось обновить статус' };
  }
  return { ok: true };
}

async function patchStatus(id: string, status: AvgVideoStatus): Promise<void> {
  const result = await patchStatusRequest(id, status);
  if (!result.ok) {
    showToast('❌ ' + (result.message || 'Ошибка'));
    return;
  }
  showToast('✅ Статус обновлён');
  await loadAdminAvgVideo();
}

async function deleteVideo(id: string): Promise<void> {
  if (!confirm('Удалить это видео?')) return;
  const r = await fetch(`/api/admin/avg-video/videos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + state.authToken },
  });
  if (!r.ok) {
    showToast('❌ Не удалось удалить видео');
    return;
  }
  selectedIds.delete(id);
  showToast('✅ Видео удалено');
  await loadAdminAvgVideo();
}

async function bulkStatus(status: AvgVideoStatus): Promise<void> {
  if (selectedIds.size === 0) return;

  const results = await Promise.all(
    Array.from(selectedIds).map((id) => patchStatusRequest(id, status))
  );

  const successful = results.filter((r) => r.ok).length;
  const failed = results.filter((r) => !r.ok).length;

  let message: string;
  if (failed === 0) {
    message = `✅ Обновлено: ${successful}`;
  } else {
    const firstError = results.find((r) => !r.ok)?.message || 'Неизвестная ошибка';
    message = `⚠️ Обновлено: ${successful}, ошибок: ${failed} — ${firstError}`;
  }
  showToast(message);

  selectedIds.clear();
  await loadAdminAvgVideo();
}

/** Применить то, что открыто инлайн в дереве рубрик (создание/переименование/удаление). */
async function applyRubricInline(action: string): Promise<void> {
  const title = $<HTMLInputElement>('admin-atv-rubric-input')?.value.trim() || '';

  if (action === 'create' && rubricInline.mode === 'add') {
    if (!title) {
      showToast('❌ Укажите название рубрики');
      return;
    }
    const parentId = rubricInline.parentId;
    const r = await fetch('/api/admin/avg-video/rubrics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken!,
      },
      body: JSON.stringify({ title, parent_id: parentId }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { message?: string };
      showToast('❌ ' + (err.message || 'Ошибка добавления'));
      return;
    }
    showToast('✅ Рубрика добавлена');
  } else if (action === 'rename' && rubricInline.mode === 'rename') {
    if (!title) {
      showToast('❌ Название не может быть пустым');
      return;
    }
    const r = await fetch(`/api/admin/avg-video/rubrics/${encodeURIComponent(rubricInline.id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken!,
      },
      body: JSON.stringify({ title }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { message?: string };
      showToast('❌ ' + (err.message || 'Ошибка редактирования'));
      return;
    }
    showToast('✅ Рубрика обновлена');
  } else if (action === 'delete' && rubricInline.mode === 'delete') {
    const r = await fetch(`/api/admin/avg-video/rubrics/${encodeURIComponent(rubricInline.id)}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { message?: string };
      showToast('❌ ' + (err.message || 'Не удалось удалить'));
      return;
    }
    showToast('✅ Рубрика удалена');
  } else {
    return;
  }

  rubricInline = { mode: 'idle' };
  await loadRubrics();
}

async function putRubricSortOrder(id: string, sortOrder: number): Promise<boolean> {
  const r = await fetch(`/api/admin/avg-video/rubrics/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify({ sort_order: sortOrder }),
  });
  return r.ok;
}

/**
 * Переставить рубрику на её уровне: обмен значениями `sort_order` с соседом.
 *
 * У соседей нередко один и тот же порядок (сид проставляет нули) — обмен там
 * ничего бы не изменил, поэтому в этом случае уровень перенумеровывается по
 * позициям: видимый порядок остаётся прежним, а значения становятся разными.
 */
async function moveRubric(id: string, delta: -1 | 1): Promise<void> {
  const rubric = adminRubricsCache.find((r) => r.id === id);
  if (!rubric) return;

  const siblings = rubricSiblings(rubric.parent_id);
  const index = siblings.findIndex((r) => r.id === id);
  const neighbour = siblings[index + delta];
  if (!neighbour) return;

  const ordered = siblings.slice();
  ordered[index] = neighbour;
  ordered[index + delta] = rubric;

  const updates = rubric.sort_order === neighbour.sort_order
    ? ordered
      .map((r, i) => ({ id: r.id, sort_order: i }))
      .filter((u, i) => ordered[i].sort_order !== u.sort_order)
    : [
      { id: rubric.id, sort_order: neighbour.sort_order },
      { id: neighbour.id, sort_order: rubric.sort_order },
    ];

  const results = await Promise.all(updates.map((u) => putRubricSortOrder(u.id, u.sort_order)));
  if (results.some((ok) => !ok)) {
    showToast('❌ Не удалось изменить порядок рубрик');
  }
  await loadRubrics();
}

function setRubricInline(next: RubricInline): void {
  rubricInline = next;
  renderRubricsTree();
}

/**
 * Поля формы по виду файла. `poster` — картинка, остальные два — видео;
 * механика загрузки у всех одна (одноразовая ссылка Диска + PUT из браузера).
 */
const UPLOAD_FIELDS: Record<'video' | 'preview' | 'poster', { btn: string; file: string; url: string }> = {
  video: { btn: 'btn-admin-atv-upload-video', file: 'admin-atv-upload-video-file', url: 'admin-atv-video-url' },
  preview: { btn: 'btn-admin-atv-upload-preview', file: 'admin-atv-upload-preview-file', url: 'admin-atv-preview-url' },
  poster: { btn: 'btn-admin-atv-upload-poster', file: 'admin-atv-upload-poster-file', url: 'admin-atv-thumbnail-url' },
};

async function uploadAvgVideoFile(file: File, kind: 'video' | 'preview' | 'poster'): Promise<void> {
  const ids = UPLOAD_FIELDS[kind];
  const btn = $<HTMLButtonElement>(ids.btn);
  const input = $<HTMLInputElement>(ids.file);
  const urlField = $<HTMLInputElement>(ids.url);

  if (!btn || !input || !urlField) return;

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Загрузка…';

  try {
    // Шаг 1: у шлюза берём одноразовую ссылку Диска (токен наружу не уходит).
    const query = `kind=${encodeURIComponent(kind)}&filename=${encodeURIComponent(file.name)}`;
    const hrefResp = await fetch(`/api/admin/avg-video/upload/href?${query}`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + state.authToken },
    });

    if (!hrefResp.ok) {
      const err = await hrefResp.json().catch(() => ({})) as { message?: string };
      showToast('❌ ' + (err.message || `Ошибка загрузки (${hrefResp.status})`));
      return;
    }

    const { upload_href, url, disk_space } = await hrefResp.json() as {
      upload_href: string;
      url: string;
      disk_space?: AvgVideoDiskSpace | null;
    };

    // AV-HC-6: об остатке места узнаём до отправки файла, а не по отказу Диска
    // на последнем проценте гигабайтной выгрузки.
    if (disk_space?.low) {
      showToast(
        `⚠️ На Яндекс.Диске свободно ${formatDiskBytes(disk_space.free_bytes)}`
        + ` (${disk_space.free_percent}%) — меньше ${disk_space.low_threshold_percent}%`,
        { variant: 'error' }
      );
    }

    // Шаг 2: сам файл летит из браузера прямо в Диск. Через шлюз его гнать нельзя:
    // приём тела обрывается на 5-й минуте, а 200-мегабайтный ролик столько и идёт.
    await putFileToStorage(file, upload_href, (percent) => {
      // После 100 % Диск ещё несколько секунд закрывает файл — молчать в это время нельзя.
      btn.textContent = percent < 100 ? `Загрузка… ${percent}%` : 'Сохраняем на Диске…';
    });

    urlField.value = url;
    showToast(kind === 'poster' ? '✅ Картинка загружена на Диск' : '✅ Файл загружен на Диск');

    // Сбросить file input
    input.value = '';
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  } finally {
    btn.disabled = false;
    btn.textContent = originalText;
  }
}

/** PUT файла в хранилище с процентами: fetch прогресс отдачи не показывает, XHR — показывает. */
function putFileToStorage(file: File, href: string, onProgress: (percent: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', href, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.floor((e.loaded / e.total) * 100));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Хранилище отклонило файл (${xhr.status})`));
    });
    xhr.addEventListener('error', () => reject(new Error('Не удалось передать файл в хранилище')));
    xhr.addEventListener('abort', () => reject(new Error('Загрузка прервана')));

    xhr.send(file);
  });
}

function formatDiskBytes(bytes: number): string {
  if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(2)} ТБ`;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} ГБ`;
  return `${(bytes / 1024 ** 2).toFixed(0)} МБ`;
}

/** Строка об остатке места на Диске; при остатке меньше порога — предупреждение (AV-HC-6). */
function diskSpaceNote(space: AvgVideoDiskSpace | null | undefined): string {
  if (!space) return '';
  const base = ` Место на Диске: свободно ${formatDiskBytes(space.free_bytes)}`
    + ` из ${formatDiskBytes(space.total_bytes)} (${space.free_percent}%).`;
  return space.low
    ? `${base} ⚠️ Осталось меньше ${space.low_threshold_percent}% — освободите место, иначе выгрузка перестанет проходить.`
    : base;
}

function renderStorageSettings(settings: AvgVideoStorageSettings): void {
  const statusEl = $('admin-atv-storage-status');
  if (statusEl) {
    // Источник важен: реквизиты разрешаются по цепочке «сохранённое → env
    // сервера», и без этой подписи админ не отличит рабочий ключ из env от
    // ненастроенного хранилища, а сохранение своего значения молча перекроет env.
    const fromEnv = settings.token_source === 'env';
    const rootNote = settings.root_source === 'env' || settings.root_source === 'default'
      ? ` Корневая папка: ${settings.root_path_effective || '—'} (${settings.root_source === 'env' ? 'из env сервера' : 'умолчание'}).`
      : '';

    const spaceNote = diskSpaceNote(settings.disk_space);

    if (!settings.has_token) {
      statusEl.textContent = 'Хранилище не настроено — укажите токен доступа.';
    } else if (fromEnv) {
      statusEl.textContent = `Действует токен из env сервера: ${settings.token_masked}. `
        + `Здесь он не сохранён — введите свой, чтобы перекрыть значение из env.${rootNote}${spaceNote}`;
    } else {
      statusEl.textContent = `Токен сохранён: ${settings.token_masked}. `
        + `Чтобы оставить его без изменений, не заполняйте поле «Токен доступа».${rootNote}${spaceNote}`;
    }
  }
  // Поле токена никогда не заполняется значением с сервера — там только маска в подписи выше.
  const tokenInput = $<HTMLInputElement>('admin-atv-storage-token');
  if (tokenInput) tokenInput.value = '';
  const rootInput = $<HTMLInputElement>('admin-atv-storage-root');
  if (rootInput) rootInput.value = settings.root_path || '';
}

async function loadAdminAvgVideoStorage(): Promise<void> {
  const statusEl = $('admin-atv-storage-status');
  if (statusEl) statusEl.textContent = 'Загрузка…';
  try {
    const r = await fetch('/api/admin/avg-video/upload/settings', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось загрузить настройки хранилища');
    const settings = await r.json() as AvgVideoStorageSettings;
    renderStorageSettings(settings);
  } catch (err) {
    if (statusEl) statusEl.textContent = 'Ошибка загрузки настроек хранилища';
    showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
  }
}

async function saveAdminAvgVideoStorage(): Promise<void> {
  const btn = $<HTMLButtonElement>('btn-admin-atv-storage-save');
  const tokenInput = $<HTMLInputElement>('admin-atv-storage-token');
  const rootInput = $<HTMLInputElement>('admin-atv-storage-root');

  const payload: { token?: string; root_path?: string } = {
    root_path: rootInput?.value.trim() || '',
  };
  // Пустое поле токена — сигнал «не менял»: PUT его не затрёт (см. контроллер).
  const tokenValue = tokenInput?.value || '';
  if (tokenValue) payload.token = tokenValue;

  if (btn) btn.disabled = true;
  try {
    const r = await fetch('/api/admin/avg-video/upload/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken!,
      },
      body: JSON.stringify(payload),
    });

    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { detail?: string; message?: string };
      showToast(err.detail || err.message || 'Не удалось сохранить настройки хранилища', { variant: 'error' });
      return;
    }

    const settings = await r.json() as AvgVideoStorageSettings;
    renderStorageSettings(settings);
    showToast('Настройки хранилища сохранены');
  } catch (err) {
    showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
  } finally {
    if (btn) btn.disabled = false;
  }
}

function bindAdminFilters(): void {
  if (adminFiltersBound) return;
  adminFiltersBound = true;

  bindAutoGrowTextareas();

  ['admin-atv-search', 'admin-atv-filter-status', 'admin-atv-filter-rubric', 'admin-atv-filter-sort'].forEach((id) => {
    $(id)?.addEventListener('input', () => renderTable());
    $(id)?.addEventListener('change', () => renderTable());
  });

  $('btn-admin-atv-new')?.addEventListener('click', () => void openEditor(null));
  $('btn-admin-atv-cancel')?.addEventListener('click', () => showEditor(false));
  $('btn-admin-atv-save')?.addEventListener('click', () => void saveEditor());
  $('btn-admin-atv-import')?.addEventListener('click', () => showToast('Импорт — в следующей версии'));
  $('btn-admin-atv-rubric-add')?.addEventListener('click', () => {
    setRubricInline(rubricInline.mode === 'add' && rubricInline.parentId === null
      ? { mode: 'idle' }
      : { mode: 'add', parentId: null });
  });

  // ── Размещение в каталоге ──────────────────────────────────────────
  $('admin-atv-direction')?.addEventListener('change', () => {
    // Подрубрики принадлежат направлению: сменили направление — прежние сняты.
    editorSubrubricIds = new Set();
    renderSubrubrics();
  });

  $('admin-atv-subrubrics')?.addEventListener('change', (e) => {
    const cb = (e.target as HTMLElement).closest<HTMLInputElement>('.admin-atv-subrubric-check');
    if (!cb) return;
    if (cb.checked) editorSubrubricIds.add(cb.value);
    else editorSubrubricIds.delete(cb.value);
  });

  $('admin-atv-collections')?.addEventListener('change', (e) => {
    const cb = (e.target as HTMLElement).closest<HTMLInputElement>('.admin-atv-collection-check');
    if (!cb) return;
    if (cb.checked) editorCollectionIds.add(cb.value);
    else editorCollectionIds.delete(cb.value);
  });

  $('admin-atv-series')?.addEventListener('change', (e) => {
    const seriesId = (e.target as HTMLSelectElement).value;
    void loadEditorSeasons(seriesId, null);
  });

  $('admin-atv-related-search')?.addEventListener('input', () => renderRelatedList());

  $('admin-atv-related')?.addEventListener('change', (e) => {
    const cb = (e.target as HTMLElement).closest<HTMLInputElement>('.admin-atv-related-check');
    if (!cb) return;
    if (cb.checked) editorRelatedIds.add(cb.value);
    else editorRelatedIds.delete(cb.value);
  });

  $('admin-atv-check-all')?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    filteredVideos().forEach((v) => {
      if (checked) selectedIds.add(v.id);
      else selectedIds.delete(v.id);
    });
    renderTable();
  });

  $('admin-atv-bulk-publish')?.addEventListener('click', () => void bulkStatus('published'));
  $('admin-atv-bulk-unpublish')?.addEventListener('click', () => void bulkStatus('archived'));
  $('admin-atv-bulk-delete')?.addEventListener('click', async () => {
    if (!confirm(`Удалить выбранные (${selectedIds.size})?`)) return;
    for (const id of [...selectedIds]) {
      await deleteVideo(id);
    }
  });

  $('admin-atv-table-body')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const editBtn = target.closest<HTMLElement>('[data-atv-edit]');
    if (editBtn?.dataset.atvEdit) {
      void openEditor(editBtn.dataset.atvEdit);
      return;
    }
    const statusBtn = target.closest<HTMLElement>('[data-atv-status]');
    if (statusBtn?.dataset.atvStatus && statusBtn.dataset.status) {
      void patchStatus(statusBtn.dataset.atvStatus, statusBtn.dataset.status as AvgVideoStatus);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-atv-delete]');
    if (delBtn?.dataset.atvDelete) {
      void deleteVideo(delBtn.dataset.atvDelete);
    }
  });

  $('admin-atv-table-body')?.addEventListener('change', (e) => {
    const cb = (e.target as HTMLElement).closest<HTMLInputElement>('.admin-atv-row-check');
    if (!cb?.dataset.id) return;
    if (cb.checked) selectedIds.add(cb.dataset.id);
    else selectedIds.delete(cb.dataset.id);
    updateBulkBar();
  });

  $('admin-atv-rubrics-tree')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    const applyBtn = target.closest<HTMLElement>('[data-atv-rubric-apply]');
    if (applyBtn?.dataset.atvRubricApply) {
      void applyRubricInline(applyBtn.dataset.atvRubricApply);
      return;
    }
    if (target.closest('[data-atv-rubric-cancel]')) {
      setRubricInline({ mode: 'idle' });
      return;
    }
    const upBtn = target.closest<HTMLElement>('[data-atv-rubric-up]');
    if (upBtn?.dataset.atvRubricUp) {
      void moveRubric(upBtn.dataset.atvRubricUp, -1);
      return;
    }
    const downBtn = target.closest<HTMLElement>('[data-atv-rubric-down]');
    if (downBtn?.dataset.atvRubricDown) {
      void moveRubric(downBtn.dataset.atvRubricDown, 1);
      return;
    }
    const addChildBtn = target.closest<HTMLElement>('[data-atv-rubric-add-child]');
    if (addChildBtn?.dataset.atvRubricAddChild) {
      const parentId = addChildBtn.dataset.atvRubricAddChild;
      setRubricInline(rubricInline.mode === 'add' && rubricInline.parentId === parentId
        ? { mode: 'idle' }
        : { mode: 'add', parentId });
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-atv-rubric-edit]');
    if (editBtn?.dataset.atvRubricEdit) {
      setRubricInline({ mode: 'rename', id: editBtn.dataset.atvRubricEdit });
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-atv-rubric-delete]');
    if (delBtn?.dataset.atvRubricDelete) {
      setRubricInline({ mode: 'delete', id: delBtn.dataset.atvRubricDelete });
    }
  });

  // Enter в инлайн-поле = «Сохранить», Escape = «Отмена»: иначе ввод названия
  // рубрики требует тянуться мышью к кнопке.
  $('admin-atv-rubrics-tree')?.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    if (!(ev.target as HTMLElement).matches('#admin-atv-rubric-input')) return;
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void applyRubricInline(rubricInline.mode === 'add' ? 'create' : 'rename');
    } else if (ev.key === 'Escape') {
      setRubricInline({ mode: 'idle' });
    }
  });

  $('btn-admin-atv-upload-video')?.addEventListener('click', () => {
    $<HTMLInputElement>('admin-atv-upload-video-file')?.click();
  });

  $('admin-atv-upload-video-file')?.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (input.files?.[0]) {
      void uploadAvgVideoFile(input.files[0], 'video');
    }
  });

  $('btn-admin-atv-upload-preview')?.addEventListener('click', () => {
    $<HTMLInputElement>('admin-atv-upload-preview-file')?.click();
  });

  $('admin-atv-upload-preview-file')?.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (input.files?.[0]) {
      void uploadAvgVideoFile(input.files[0], 'preview');
    }
  });

  $('btn-admin-atv-upload-poster')?.addEventListener('click', () => {
    $<HTMLInputElement>('admin-atv-upload-poster-file')?.click();
  });

  $('admin-atv-upload-poster-file')?.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (input.files?.[0]) {
      void uploadAvgVideoFile(input.files[0], 'poster');
    }
  });
}

/**
 * Вкладка «Инфраструктура видео»: реквизиты Диска живут отдельно от карточек
 * (AV-ADR-2, решение 7). Параметров горячего кеша тут нет — они env-only.
 */
let storageBound = false;

export async function loadAdminAvgVideoInfra(): Promise<void> {
  if (!storageBound) {
    storageBound = true;
    $('btn-admin-atv-storage-save')?.addEventListener('click', () => void saveAdminAvgVideoStorage());
  }
  await loadAdminAvgVideoStorage();
}

export async function loadAdminAvgVideo(): Promise<void> {
  bindAdminFilters();

  const tbody = $('admin-atv-table-body');
  if (tbody) tbody.innerHTML = '<tr><td colspan="7" class="audit-log-empty">Загрузка...</td></tr>';

  try {
    const [listRes, statsRes] = await Promise.all([
      fetch('/api/admin/avg-video/videos', { headers: { Authorization: 'Bearer ' + state.authToken } }),
      fetch('/api/admin/avg-video/videos/stats', { headers: { Authorization: 'Bearer ' + state.authToken } }),
    ]);
    if (!listRes.ok) throw new Error('Не удалось загрузить список видео');
    adminVideosCache = parseItems<AvgVideoVideo>(await listRes.json());
    adminStatsCache = statsRes.ok ? await statsRes.json() as Record<AvgVideoStatus, number> : null;
    updateStats();
    renderTable();
    renderRelatedList();
    await Promise.all([loadRubrics(), loadCollectionsForEditor()]);
  } catch (err) {
    if (tbody) tbody.innerHTML = '<tr><td colspan="7" class="audit-log-empty">Ошибка загрузки</td></tr>';
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

export function bindAdminAvgVideoEvents(): void {
  bindAdminFilters();
}
